// Top HUD row: run status (sector, route progress, scrap, active synergies) at
// the top-left, the room challenge tracker at the top-right and the boss bar
// at the top-centre.

import Phaser from 'phaser';
import { iconKey } from '../../art/keys';
import { COLORS, css, mix } from '../../config/palette';
import type { ChallengeState } from '../../core/contracts';
import type { RouteGraph, SynergyId } from '../../core/types';
import { formatInt, synergyColor, synergyName } from '../format';
import { Panel, TYPE, chamferPoints, createChip, glowTexture, iconImage, txt } from '../kit';

// ───────────────────────────── Run status (top-left) ─────────────────────────────

/** Sector name, route progress pips, scrap counter and synergy chips. Origin = top-left. */
export class StatusHud extends Phaser.GameObjects.Container {
  private readonly panel: Panel;
  private readonly sectorText: Phaser.GameObjects.Text;
  private readonly subText: Phaser.GameObjects.Text;
  private readonly pips: Phaser.GameObjects.Graphics;
  private readonly scrapText: Phaser.GameObjects.Text;
  private readonly scrapIcon: Phaser.GameObjects.Image;
  private readonly deltaText: Phaser.GameObjects.Text;
  private readonly chips: Phaser.GameObjects.Container;
  private readonly depthText: Phaser.GameObjects.Text;
  /** Resting scale of the scrap icon (pulses always return here). */
  private readonly scrapIconScale: number;
  private scrapShown = 0;
  private scrapTarget = 0;
  private routeKey = '';
  private synergyKey = '';
  private accent: number;

  constructor(scene: Phaser.Scene, x: number, y: number, accent: number) {
    super(scene, x, y);
    this.accent = accent;
    this.panel = new Panel(scene, 0, 0, 440, 132, { fillAlpha: 0.6, accent, ticks: true, chamfer: 12 });
    this.sectorText = txt(scene, 20, 12, '', { font: 'display', size: TYPE.h3, weight: 700, spacing: 3, color: 0xffffff });
    this.subText = txt(scene, 20, 46, '', { font: 'display', size: TYPE.micro, weight: 600, spacing: 2, color: COLORS.uiDim });
    this.pips = scene.add.graphics();
    this.scrapIcon = iconImage(scene, 36, 106, iconKey.scrap, 28, COLORS.scrap);
    this.scrapIconScale = this.scrapIcon.scaleX;
    this.scrapText = txt(scene, 58, 106, '0', { font: 'display', size: 28, weight: 700, color: COLORS.scrap, originY: 0.5 });
    this.deltaText = txt(scene, 0, 106, '', { font: 'display', size: TYPE.label, weight: 700, color: COLORS.uiGood, originY: 0.5 }).setAlpha(0);
    this.chips = scene.add.container(0, 150);
    this.depthText = txt(scene, 420, 94, '', { font: 'display', size: TYPE.micro, weight: 700, color: COLORS.uiDim, spacing: 2, originX: 1 });
    this.add([this.panel, this.sectorText, this.subText, this.depthText, this.pips, this.scrapIcon, this.scrapText, this.deltaText, this.chips]);
    scene.add.existing(this);
  }

  setAccent(accent: number): void {
    if (accent === this.accent) return;
    this.accent = accent;
    this.panel.setStyle({ accent });
    this.routeKey = '';
  }

  setSector(name: string, sectorIndex: number, sectorCount: number): void {
    if (this.sectorText.text !== name.toUpperCase()) this.sectorText.setText(name.toUpperCase());
    const sub = `SECTOR ${sectorIndex + 1} / ${sectorCount}`;
    if (this.subText.text !== sub) this.subText.setText(sub);
  }

  /** Route progress: one pip per depth after the start; the last pip is the boss. */
  setRoute(graph: RouteGraph, currentId: string): void {
    const node = graph.nodes[currentId];
    const depth = node?.depth ?? 0;
    const last = Math.max(1, graph.depthCount - 1);
    const key = `${graph.sector}:${currentId}:${depth}:${last}:${this.accent}`;
    if (key === this.routeKey) return;
    this.routeKey = key;
    const g = this.pips;
    g.clear();
    const x0 = 214;
    const y = 58;
    const step = (440 - 20 - x0 - 14) / last;
    g.lineStyle(2, COLORS.uiBorder, 1);
    g.lineBetween(x0, y, x0 + step * last, y);
    if (depth > 0) {
      g.lineStyle(2, this.accent, 0.9);
      g.lineBetween(x0, y, x0 + step * Math.min(depth, last), y);
    }
    for (let d = 0; d <= last; d++) {
      const x = x0 + d * step;
      const boss = d === last;
      const done = d < depth;
      const cur = d === depth;
      const s = boss ? 8 : cur ? 7 : 5;
      const col = boss ? COLORS.threat : cur ? 0xffffff : done ? this.accent : COLORS.steel3;
      if (cur) {
        g.fillStyle(this.accent, 0.3);
        g.fillCircle(x, y, 13);
      }
      g.fillStyle(done || cur || boss ? col : 0x070a0f, 1);
      g.fillPoints([new Phaser.Math.Vector2(x, y - s), new Phaser.Math.Vector2(x + s, y), new Phaser.Math.Vector2(x, y + s), new Phaser.Math.Vector2(x - s, y)], true);
      g.lineStyle(1.5, col, 1);
      g.strokePoints([new Phaser.Math.Vector2(x, y - s), new Phaser.Math.Vector2(x + s, y), new Phaser.Math.Vector2(x, y + s), new Phaser.Math.Vector2(x - s, y)], true, true);
    }
    this.depthText.setText(`ROOM ${depth} / ${last}`);
  }

  /** Animate towards a new scrap total; a positive/negative delta floats beside the counter. */
  setScrap(total: number, delta?: number): void {
    if (total === this.scrapTarget && delta === undefined) return;
    this.scrapTarget = total;
    if (delta) {
      this.deltaText.setText(`${delta > 0 ? '+' : '−'}${formatInt(Math.abs(delta))}`).setColor(css(delta > 0 ? COLORS.uiGood : COLORS.uiBad));
      this.deltaText.setX(this.scrapText.x + Math.max(this.scrapText.width, 60) + 14).setAlpha(1);
      this.scene.tweens.killTweensOf(this.deltaText);
      this.deltaText.y = 106;
      this.scene.tweens.add({ targets: this.deltaText, alpha: 0, y: 92, delay: 700, duration: 600 });
      // Restart from the resting scale: reading the current (mid-pulse) scale would compound on rapid pickups.
      this.scene.tweens.killTweensOf(this.scrapIcon);
      const s = this.scrapIconScale;
      this.scrapIcon.setScale(s);
      this.scene.tweens.add({ targets: this.scrapIcon, scale: { from: s * 1.35, to: s }, duration: 260, ease: 'Back.easeOut' });
    }
  }

  setSynergies(ids: readonly SynergyId[]): void {
    const key = ids.join(',');
    if (key === this.synergyKey) return;
    const added = ids.filter((id) => !this.synergyKey.split(',').includes(id));
    this.synergyKey = key;
    this.chips.removeAll(true);
    let x = 0;
    for (const id of ids) {
      const chip = createChip(this.scene, x, 0, synergyName(id), synergyColor(id), { size: 'sm', filled: true });
      this.chips.add(chip);
      if (added.includes(id)) {
        chip.setAlpha(0);
        this.scene.tweens.add({ targets: chip, alpha: 1, x: { from: x - 16, to: x }, duration: 320, ease: 'Cubic.easeOut' });
      }
      x += chip.width + 8;
    }
  }

  tick(dt: number): void {
    if (this.scrapShown !== this.scrapTarget) {
      const diff = this.scrapTarget - this.scrapShown;
      const step = Math.sign(diff) * Math.max(1, Math.abs(diff) * Math.min(1, dt * 9));
      this.scrapShown = Math.abs(step) >= Math.abs(diff) ? this.scrapTarget : this.scrapShown + step;
      this.scrapText.setText(formatInt(this.scrapShown));
    }
  }

  /** Jump straight to a value (first frame). */
  initScrap(total: number): void {
    this.scrapShown = this.scrapTarget = total;
    this.scrapText.setText(formatInt(total));
  }
}

// ───────────────────────────── Challenge tracker (top-right) ─────────────────────────────

/** Optional room challenge: text, timer bar, completed / failed states. Origin = top-right corner. */
export class ChallengeTracker extends Phaser.GameObjects.Container {
  static readonly W = 430;
  private readonly bg: Phaser.GameObjects.Graphics;
  private readonly head: Phaser.GameObjects.Text;
  private readonly stateText: Phaser.GameObjects.Text;
  private readonly bodyText: Phaser.GameObjects.Text;
  private readonly bar: Phaser.GameObjects.Graphics;
  private readonly timer: Phaser.GameObjects.Text;
  private current: { id: string; status: ChallengeState['status']; text: string } | null = null;
  private dismissed = '';
  private maxTime = 0;
  private lastTime = -1;
  private hideAt = 0;
  private h = 120;
  private elapsed = 0;

  constructor(scene: Phaser.Scene, rightX: number, y: number) {
    super(scene, rightX - ChallengeTracker.W, y);
    const W = ChallengeTracker.W;
    this.bg = scene.add.graphics();
    this.head = txt(scene, 22, 14, 'CHALLENGE', { font: 'display', size: TYPE.micro, weight: 700, color: COLORS.telegraph, spacing: 3 });
    this.stateText = txt(scene, W - 22, 14, '', { font: 'display', size: TYPE.micro, weight: 700, color: COLORS.uiDim, spacing: 2, originX: 1 });
    this.bodyText = txt(scene, 22, 42, '', { size: TYPE.body, color: COLORS.uiText, wrap: W - 44, lineSpacing: -2 });
    this.bar = scene.add.graphics();
    this.timer = txt(scene, W - 22, 0, '', { font: 'display', size: TYPE.label, weight: 700, color: 0xffffff, originX: 1 });
    this.add([this.bg, this.head, this.stateText, this.bodyText, this.bar, this.timer]);
    this.setVisible(false).setAlpha(0);
    scene.add.existing(this);
  }

  /** Feed the world's current challenge (null = none in this room). */
  set(c: ChallengeState | null): void {
    if (!c) {
      if (this.current && this.current.status === 'active') this.hide();
      if (this.current && this.current.status !== 'active' && this.elapsed > this.hideAt) this.hide();
      return;
    }
    const cur = this.current;
    const isNew = !cur || cur.id !== c.id || cur.text !== c.text;
    // A resolved challenge that already faded out stays dismissed while the world keeps reporting it.
    if (isNew && c.status !== 'active' && this.dismissed === `${c.id}|${c.text}`) return;
    if (isNew || !cur) {
      this.current = { id: c.id, status: c.status, text: c.text };
      this.maxTime = c.timeLeft ?? 0;
      this.lastTime = -1;
      this.bodyText.setText(c.text);
      this.show();
      this.applyStatus(c.status, false);
    } else if (c.status !== cur.status) {
      cur.status = c.status;
      this.applyStatus(c.status, true);
    }
    if (c.timeLeft !== undefined && c.status === 'active') {
      this.maxTime = Math.max(this.maxTime, c.timeLeft);
      const t = Math.ceil(c.timeLeft * 10) / 10;
      if (t !== this.lastTime) {
        this.lastTime = t;
        this.timer.setText(`${Math.max(0, c.timeLeft).toFixed(1)}s`).setColor(css(c.timeLeft < 5 ? COLORS.uiBad : 0xffffff));
        this.drawBar(this.maxTime > 0 ? c.timeLeft / this.maxTime : 0, c.timeLeft < 5 ? COLORS.uiBad : COLORS.telegraph);
      }
    } else if (c.status === 'active' && this.lastTime !== -2) {
      this.lastTime = -2;
      this.timer.setText('');
      this.bar.clear();
    }
  }

  /** Bus events can arrive before the next poll; mirror them immediately. */
  event(kind: 'started' | 'completed' | 'failed', id: string, text: string): void {
    if (kind === 'started') this.set({ id, text, status: 'active' });
    else this.set({ id, text, status: kind === 'completed' ? 'completed' : 'failed' });
  }

  private applyStatus(status: ChallengeState['status'], animate: boolean): void {
    const col = status === 'completed' ? COLORS.uiGood : status === 'failed' ? COLORS.uiBad : COLORS.telegraph;
    this.stateText.setText(status === 'completed' ? 'COMPLETE' : status === 'failed' ? 'FAILED' : 'ACTIVE').setColor(css(status === 'active' ? COLORS.uiDim : col));
    this.head.setColor(css(col));
    this.bodyText.setColor(css(status === 'failed' ? COLORS.uiDim : COLORS.uiText));
    if (status !== 'active') {
      this.timer.setText('');
      this.drawBar(status === 'completed' ? 1 : 0, col);
      this.hideAt = this.elapsed + 4;
    }
    this.drawFrame(col, status !== 'active');
    if (animate) {
      this.scene.tweens.add({ targets: this, scale: { from: 1.04, to: 1 }, duration: 260, ease: 'Back.easeOut' });
    }
  }

  private drawFrame(col: number, resolved: boolean): void {
    const W = ChallengeTracker.W;
    this.h = 42 + this.bodyText.height + 36;
    const g = this.bg;
    g.clear();
    g.fillStyle(mix(COLORS.uiPanel, col, resolved ? 0.12 : 0.04), 0.78);
    g.fillPoints(chamferPoints(0, 0, W, this.h, { tr: 12, bl: 12 }), true);
    g.lineStyle(1.5, resolved ? col : COLORS.uiBorder, 1);
    g.strokePoints(chamferPoints(0, 0, W, this.h, { tr: 12, bl: 12 }), true, true);
    g.fillStyle(col, 1);
    g.fillRect(0, 10, 3, this.h - 20);
    this.timer.setY(this.h - 33);
  }

  private drawBar(f: number, col: number): void {
    const W = ChallengeTracker.W;
    const g = this.bar;
    const y = this.h - 22;
    // Leave room for the countdown on the right while a timer runs.
    const bw = W - 44 - (this.timer.text ? 86 : 0);
    g.clear();
    g.fillStyle(0x070a0f, 0.9);
    g.fillRect(22, y, bw, 6);
    g.fillStyle(col, 1);
    g.fillRect(22, y, bw * Phaser.Math.Clamp(f, 0, 1), 6);
  }

  private show(): void {
    this.scene.tweens.killTweensOf(this);
    this.setVisible(true);
    this.scene.tweens.add({ targets: this, alpha: 1, duration: 220 });
  }

  private hide(): void {
    if (this.current && this.current.status !== 'active') this.dismissed = `${this.current.id}|${this.current.text}`;
    if (!this.visible) {
      this.current = null;
      return;
    }
    this.current = null;
    this.scene.tweens.killTweensOf(this);
    this.scene.tweens.add({ targets: this, alpha: 0, duration: 300, onComplete: () => this.setVisible(false) });
  }

  tick(dt: number): void {
    this.elapsed += dt;
    if (this.current && this.current.status !== 'active' && this.elapsed > this.hideAt) this.hide();
  }
}

// ───────────────────────────── Boss bar (top-centre) ─────────────────────────────

export interface BossBarState {
  name: string;
  title: string;
  hp: number;
  maxHp: number;
  armor: number;
  phase: number;
  phaseCount: number;
}

/** Large boss health bar with phase ticks at 66 % / 33 %. Origin = top-centre. */
export class BossBar extends Phaser.GameObjects.Container {
  static readonly W = 860;
  private readonly nameText: Phaser.GameObjects.Text;
  private readonly titleText: Phaser.GameObjects.Text;
  private readonly phaseText: Phaser.GameObjects.Text;
  private readonly g: Phaser.GameObjects.Graphics;
  private readonly glow: Phaser.GameObjects.Image;
  private shown = false;
  private hp = 1;
  private ghost = 1;
  private ghostHold = 0;
  private armor = 0;
  private flashT = 0;
  private phaseCount = 3;
  private targetHp = 1;
  private dirty = true;

  constructor(scene: Phaser.Scene, cx: number, y: number) {
    super(scene, cx, y);
    this.glow = scene.add.image(0, 70, glowTexture(scene)).setDisplaySize(BossBar.W * 1.1, 120).setTint(COLORS.threat).setAlpha(0.18).setBlendMode(Phaser.BlendModes.ADD);
    this.nameText = txt(scene, 0, 0, '', { font: 'display', size: 34, weight: 700, color: 0xffffff, spacing: 6, originX: 0.5, glow: { color: COLORS.threat, blur: 14, alpha: 0.7 } });
    this.titleText = txt(scene, 0, 42, '', { font: 'display', size: TYPE.micro, weight: 600, color: COLORS.threatGlow, spacing: 4, originX: 0.5 });
    this.phaseText = txt(scene, BossBar.W / 2, 104, '', { font: 'display', size: TYPE.micro, weight: 700, color: COLORS.uiDim, spacing: 2, originX: 1 });
    this.g = scene.add.graphics();
    this.add([this.glow, this.nameText, this.titleText, this.g, this.phaseText]);
    this.setVisible(false).setAlpha(0);
    scene.add.existing(this);
  }

  get isShown(): boolean {
    return this.shown;
  }

  show(s: BossBarState): void {
    this.nameText.setText(s.name.toUpperCase());
    this.titleText.setText(s.title.toUpperCase());
    this.shown = true;
    this.setVisible(true);
    this.scene.tweens.killTweensOf(this);
    this.y = 6;
    this.scene.tweens.add({ targets: this, alpha: 1, y: 26, duration: 420, ease: 'Cubic.easeOut' });
    // Intro: the bar fills up from empty.
    this.hp = 0;
    this.ghost = 0;
    this.set(s);
  }

  hide(): void {
    if (!this.shown) return;
    this.shown = false;
    this.scene.tweens.killTweensOf(this);
    this.scene.tweens.add({ targets: this, alpha: 0, y: 6, duration: 500, delay: 400, onComplete: () => this.setVisible(false) });
  }

  set(s: BossBarState): void {
    const f = s.maxHp > 0 ? Phaser.Math.Clamp(s.hp / s.maxHp, 0, 1) : 0;
    const a = s.maxHp > 0 ? Phaser.Math.Clamp(s.armor / s.maxHp, 0, 1) : 0;
    if (f < this.hp - 0.0005) {
      this.ghost = Math.max(this.ghost, this.hp);
      this.ghostHold = 0.4;
      this.hp = f;
      this.dirty = true;
    } else if (f > this.hp + 0.0005) {
      // Rising (intro fill / heal) animates in tick().
      this.dirty = true;
    }
    this.targetHp = f;
    if (a !== this.armor) {
      this.armor = a;
      this.dirty = true;
    }
    if (s.phaseCount !== this.phaseCount) {
      this.phaseCount = s.phaseCount;
      this.dirty = true;
    }
    const pt = s.phaseCount > 1 ? `PHASE ${s.phase} / ${s.phaseCount}` : '';
    if (this.phaseText.text !== pt) this.phaseText.setText(pt);
  }

  phaseFlash(): void {
    this.flashT = 0.5;
    this.dirty = true;
    this.scene.tweens.add({ targets: this.nameText, scale: { from: 1.12, to: 1 }, duration: 380, ease: 'Back.easeOut' });
  }

  tick(dt: number): void {
    if (!this.visible) return;
    if (this.hp < this.targetHp) {
      this.hp = Math.min(this.targetHp, this.hp + dt * 1.6);
      if (this.ghost < this.hp) this.ghost = this.hp;
      this.dirty = true;
    }
    if (this.ghost > this.hp) {
      if (this.ghostHold > 0) this.ghostHold -= dt;
      else this.ghost = Math.max(this.hp, this.ghost - dt * 0.6);
      this.dirty = true;
    }
    if (this.flashT > 0) {
      this.flashT = Math.max(0, this.flashT - dt);
      this.dirty = true;
    }
    if (this.dirty) this.redraw();
  }

  private redraw(): void {
    this.dirty = false;
    const W = BossBar.W;
    const x0 = -W / 2;
    const y = 72;
    const h = 22;
    const g = this.g;
    g.clear();
    g.fillStyle(0x070a0f, 0.88);
    g.fillPoints(chamferPoints(x0 - 6, y - 6, W + 12, h + 12, { tl: 10, br: 10 }), true);
    g.lineStyle(1.5, mix(COLORS.uiBorder, COLORS.threat, 0.5), 1);
    g.strokePoints(chamferPoints(x0 - 6, y - 6, W + 12, h + 12, { tl: 10, br: 10 }), true, true);
    if (this.ghost > this.hp) {
      g.fillStyle(0xffe0e6, 0.75);
      g.fillRect(x0 + W * this.hp, y, W * (this.ghost - this.hp), h);
    }
    const col = this.flashT > 0 ? mix(COLORS.threat, 0xffffff, this.flashT) : COLORS.threat;
    g.fillStyle(col, 1);
    g.fillRect(x0, y, W * this.hp, h);
    g.fillStyle(0xffffff, 0.25);
    g.fillRect(x0, y, W * this.hp, 4);
    g.fillStyle(0x000000, 0.2);
    g.fillRect(x0, y + h - 5, W * this.hp, 5);
    if (this.armor > 0) {
      g.fillStyle(COLORS.shieldBar, 0.9);
      g.fillRect(x0, y - 4, W * this.armor, 4);
    }
    // Phase thresholds.
    const ticks = this.phaseCount >= 3 ? [2 / 3, 1 / 3] : this.phaseCount === 2 ? [0.5] : [];
    for (const t of ticks) {
      const tx = x0 + W * t;
      g.fillStyle(0x070a0f, 1);
      g.fillRect(tx - 2, y, 4, h);
      g.fillStyle(this.hp > t ? 0xffffff : COLORS.steel3, 1);
      g.fillTriangle(tx - 6, y - 10, tx + 6, y - 10, tx, y - 2);
      g.fillTriangle(tx - 6, y + h + 10, tx + 6, y + h + 10, tx, y + h + 2);
    }
  }
}
